import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatUZS } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, Link2, Loader2, Package, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import { PaginationControls } from '@/components/PaginationControls';
import { useCategories } from '@/hooks/use-categories';
import { useAssignGlobalProducts, useGlobalProducts, type GlobalProduct } from '@/hooks/use-global-products';

const PAGE_SIZE = 200;

interface Props {
  /** Admin «Merchantga biriktirish»: pick the merchant. Without it the merchant adds to itself (AssignGlobalProductsToSelf). */
  merchants?: Array<{ id: number; name: string }>;
}

/** FR-98: Lovable's AssignGlobalProducts / AssignGlobalProductsToSelf in one component. */
export default function AssignGlobalProducts({ merchants }: Props) {
  const { t } = useTranslation();
  const otherLabel = t('misc_assign.other');
  const adminView = merchants !== undefined;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const { data: categories = [] } = useCategories();
  const { data, isLoading } = useGlobalProducts({ search: debouncedSearch, category: catFilter, page, per_page: PAGE_SIZE });
  const assign = useAssignGlobalProducts();

  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const filtered = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.lastPage ?? 1;
  const filterCategories = categories.map(c => String(c.name));

  const toggleProduct = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const allSelected = filtered.length > 0 && filtered.every(p => selected.has(p.id));
    setSelected(prev => {
      const next = new Set(prev);
      filtered.forEach(p => { if (allSelected) next.delete(p.id); else next.add(p.id); });
      return next;
    });
  };

  const toggleCategory = (category: string) => {
    const catProducts = filtered.filter(p => (p.category || otherLabel) === category);
    const allSelected = catProducts.every(p => selected.has(p.id));
    setSelected(prev => {
      const next = new Set(prev);
      catProducts.forEach(p => { if (allSelected) next.delete(p.id); else next.add(p.id); });
      return next;
    });
  };

  const handleAssign = async () => {
    if (adminView && !selectedMerchant) { toast.error(t('misc_assign.select_merchant')); return; }
    if (selected.size === 0) { toast.error(t('misc_assign.select_products')); return; }
    try {
      const result = await assign.mutateAsync({
        productIds: Array.from(selected),
        merchantId: adminView ? Number(selectedMerchant) : undefined,
      });
      const assignedCount = result?.assigned ?? 0;
      const skippedCount = result?.skipped ?? 0;
      if (assignedCount > 0) {
        toast.success(`${t(adminView ? 'misc_assign.assigned' : 'misc_assign.added', { count: assignedCount })}${skippedCount > 0 ? t('misc_assign.skipped', { count: skippedCount }) : ''}`);
      } else {
        toast.info(t('misc_assign.all_exist'));
      }
      setSelected(new Set());
    } catch (err) {
      toast.error((err as Error)?.message || t('misc_assign.error'));
    }
  };

  const grouped = filtered.reduce<Record<string, GlobalProduct[]>>((acc, p) => {
    const cat = p.category || otherLabel;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center flex-wrap">
        {adminView && (
          <Select value={selectedMerchant || '__none__'} onValueChange={v => setSelectedMerchant(v === '__none__' ? '' : v)}>
            <SelectTrigger className="w-[280px]">
              <SelectValue placeholder={t('misc_assign.select_merchant_ph')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">{t('misc_assign.select_merchant_ph')}</SelectItem>
              {merchants.map(m => <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder={t('misc_assign.search_ph')} value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>

        <Select value={catFilter} onValueChange={v => { setCatFilter(v); setPage(1); }}>
          <SelectTrigger className={adminView ? 'w-[180px]' : 'w-[200px]'}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('misc_assign.all_categories')}</SelectItem>
            {filterCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={toggleAll} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <CheckSquare size={16} />
            {selected.size > 0 ? t('misc_assign.selected_of', { selected: selected.size, total }) : t('misc_assign.select_all')}
          </button>
          {selected.size > 0 && (
            <button onClick={() => setSelected(new Set())} className="text-xs text-muted-foreground hover:text-foreground underline">
              {t('misc_assign.clear')}
            </button>
          )}
        </div>
        <Button
          onClick={handleAssign}
          disabled={(adminView && !selectedMerchant) || selected.size === 0 || assign.isPending}
          className="gap-1.5"
        >
          {assign.isPending ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
          {adminView ? t('misc_assign.assign') : t('misc_assign.add_self')} ({selected.size})
        </Button>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground py-8 text-center">{t('misc_common.loading')}</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <Package className="mx-auto mb-2 h-10 w-10 opacity-30" />
          <p>{adminView ? t('misc_assign.not_found') : t('misc_assign.global_not_found')}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([category, items]) => {
            const allCatSelected = items.every(p => selected.has(p.id));
            const someCatSelected = items.some(p => selected.has(p.id));
            return (
              <div key={category} className="bg-card border border-border rounded-xl overflow-hidden">
                <div
                  className="flex items-center gap-3 p-3 bg-muted/50 border-b border-border cursor-pointer hover:bg-muted/70 transition-colors"
                  onClick={() => toggleCategory(category)}
                >
                  <Checkbox
                    checked={allCatSelected}
                    className={someCatSelected && !allCatSelected ? 'opacity-50' : ''}
                    onCheckedChange={() => toggleCategory(category)}
                  />
                  <span className="text-sm font-semibold text-foreground">{category}</span>
                  <Badge variant="secondary" className="text-[10px]">{items.length}</Badge>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1000px]">
                    <thead>
                      <tr className="border-b border-border bg-muted/20">
                        <th className="w-10 p-3"></th>
                        <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_common.name')}</th>
                        <th className="text-left text-xs font-medium text-muted-foreground p-3">SKU</th>
                        <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_assign.th_brand')}</th>
                        <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_pschema.f_atribut_nomi')}</th>
                        <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_pschema.f_mhik')}</th>
                        <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_pschema.f_mxik_nomi')}</th>
                        <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_pschema.f_barcode')}</th>
                        <th className="text-right text-xs font-medium text-muted-foreground p-3">{t('misc_pschema.f_sale_price')}</th>
                        <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_pschema.f_unit')}</th>
                        <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_common.murabaha')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(p => (
                        <tr
                          key={p.id}
                          className={`border-b border-border last:border-0 transition-colors cursor-pointer ${selected.has(p.id) ? 'bg-primary/5' : 'hover:bg-muted/30'}`}
                          onClick={() => toggleProduct(p.id)}
                        >
                          <td className="p-3" onClick={e => e.stopPropagation()}>
                            <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleProduct(p.id)} />
                          </td>
                          <td className="p-3 text-sm text-foreground font-medium max-w-[220px] truncate" title={p.name}>{p.name}</td>
                          <td className="p-3 text-sm text-muted-foreground font-mono">{p.sku}</td>
                          <td className="p-3 text-sm text-muted-foreground max-w-[120px] truncate" title={p.brend_nomi || ''}>{p.brend_nomi || '—'}</td>
                          <td className="p-3 text-sm text-muted-foreground max-w-[140px] truncate" title={p.atribut_nomi || ''}>{p.atribut_nomi || '—'}</td>
                          <td className="p-3 text-xs text-muted-foreground font-mono">{p.mhik || '—'}</td>
                          <td className="p-3 text-sm text-muted-foreground max-w-[180px] truncate" title={p.mxik_nomi || ''}>{p.mxik_nomi || '—'}</td>
                          <td className="p-3 text-xs text-muted-foreground font-mono">{p.barcode || '—'}</td>
                          <td className="p-3 text-sm text-foreground text-right font-mono">{formatUZS(Number(p.sale_price))}</td>
                          <td className="p-3 text-sm text-muted-foreground">{p.unit}</td>
                          <td className="p-3">
                            <Badge variant={p.is_murabaha_eligible ? 'default' : 'secondary'}>
                              {p.is_murabaha_eligible ? t('misc_common.yes') : t('misc_common.no')}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <PaginationControls
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={PAGE_SIZE}
        onPrev={() => setPage(p => Math.max(1, p - 1))}
        onNext={() => setPage(p => Math.min(totalPages, p + 1))}
        hasPrev={page > 1}
        hasNext={page < totalPages}
      />
    </div>
  );
}
